import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Input, Progress, Text, Wrap, WrapItem, useToast } from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import { BoozenToast } from "../../BoozenToast";
import { SuggestedItemRow } from "./SuggestedItemRow";
import {
  computeBlockProgress,
  formatBaseAmount,
  formatShortfall,
  getBlockDisplayState,
  itemCoverage,
  resetToRedistributed,
} from "../helpers";
import { CART_BLOCKED_CATEGORIES } from "../constants";

const VISIBLE_WHEN_COLLAPSED = 4;
const SEARCH_THRESHOLD = 9;

export function ResultBlock({ result, itemQuantities, setItemQuantities }) {
  const state = getBlockDisplayState(result);
  const keyFor = (batchId) => `${result.key}:${batchId}`;
  const toast = useToast();

  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [sizeFilter, setSizeFilter] = useState(null);
  const blockRef = useRef(null);
  const prevHeightRef = useRef(null);

  function showToast(type, title, description) {
    toast({
      position: "top",
      duration: 5000,
      isClosable: true,
      render: ({ onClose }) => (
        <BoozenToast type={type} title={title} description={description} onClose={onClose} />
      ),
    });
  }

  // Out-of-stock products are deliberately part of `available_products`
  // (backend keeps every mapped product reachable), but there's no useful
  // reason to show one as an "option" here — the [+] stepper is capped at
  // `remaining_stock`, so it would just render permanently disabled at the
  // bottom of the list. Filtered out entirely rather than shown-but-blocked.
  const products = (result.available_products || []).filter((p) => p.in_stock !== false);
  const saleMode = products[0]?.sale_mode;
  const isBarrel = saleMode === "barrel";
  const isCase = saleMode === "case";
  const isCaseLiters = isCase && products[0]?.case_liters != null;
  const isCartBlocked = CART_BLOCKED_CATEGORIES.includes(result.key);

  const { currentBase, currentCases, isComplete } = computeBlockProgress(result, itemQuantities);

  // Correction C3: every mapped product stays reachable. Collapsed, show
  // whatever already has quantity on it plus the next highest-priority
  // products (available_products already arrives priority-sorted) up to 4
  // rows — but always in the original priority order, never reshuffled to
  // put selected items first. Typing a quantity into a row must never move
  // that row; it can only make an otherwise-hidden row appear in its own
  // (unchanged) slot.
  const selectedIds = new Set(
    products.filter((p) => (itemQuantities[keyFor(p.batch_id)] ?? 0) > 0).map((p) => p.batch_id)
  );
  const topPriorityIds = new Set(products.slice(0, VISIBLE_WHEN_COLLAPSED).map((p) => p.batch_id));
  const collapsedList = products.filter((p) => topPriorityIds.has(p.batch_id) || selectedIds.has(p.batch_id));
  const hasMore = products.length > collapsedList.length;

  const barrelSizes = useMemo(() => {
    if (!isBarrel) return [];
    return Array.from(new Set(products.map((p) => p.unit_volume_ml))).sort((a, b) => b - a);
  }, [isBarrel, products]);

  let visibleList = expanded ? products : collapsedList;
  if (expanded && isBarrel && sizeFilter != null) {
    visibleList = visibleList.filter((p) => p.unit_volume_ml === sizeFilter);
  }
  if (expanded && search.trim() && products.length > SEARCH_THRESHOLD) {
    const term = search.trim().toLowerCase();
    visibleList = visibleList.filter((p) => p.product_name.toLowerCase().includes(term));
  }

  // Collapsing "Ver todas as opções" (or a search/tamanho filter narrowing
  // the list) can shrink this block by a lot. If the user had scrolled past
  // its top, the browser's scroll position stays at the same absolute Y —
  // which, now that everything below shrank and shifted up, lands on
  // whatever content ended up there instead of where they were looking.
  // Compensate by the height delta so the viewport doesn't jump — but only
  // ever shrinks scrollY down to the block's OWN top, never past it: if the
  // user had scrolled deep into a long expanded list and the exact row they
  // were looking at got removed by the collapse, there's nothing left to
  // restore them to, and landing at the top of the (now short) block is a
  // far saner fallback than overshooting into whatever is above it.
  useLayoutEffect(() => {
    const el = blockRef.current;
    if (!el) return;
    const newHeight = el.getBoundingClientRect().height;
    const prevHeight = prevHeightRef.current;
    if (prevHeight != null) {
      const delta = prevHeight - newHeight; // > 0 only when the block shrank
      const top = el.getBoundingClientRect().top;
      if (delta > 0 && top < 0) {
        const compensation = Math.min(delta, -top);
        window.scrollBy(0, -compensation);
      }
    }
    prevHeightRef.current = newHeight;
  }, [expanded, visibleList.length]);

  function handleQtyChange(batchId, value, maxStock) {
    const upperBound = maxStock === undefined || maxStock === null ? value : Math.min(value, maxStock);
    const clamped = Math.max(0, upperBound);
    setItemQuantities((prev) => ({ ...prev, [keyFor(batchId)]: clamped }));
  }

  // Moves what the user had of `fromBatchId` onto `toBatchId`, converting the
  // quantity to keep roughly the same coverage (volume/units) rather than a
  // literal 1:1 count — e.g. 3 barris de 50L -> 5 barris de 30L (briefing
  // correction C3). Only moves as much as the target's remaining stock can
  // actually take: a partial/blocked transfer surfaces a toast instead of
  // silently discarding quantity.
  function handleSwap(fromBatchId, toBatchId) {
    const fromKey = keyFor(fromBatchId);
    const toKey = keyFor(toBatchId);
    const fromItem = products.find((p) => p.batch_id === fromBatchId);
    const toItem = products.find((p) => p.batch_id === toBatchId);
    if (!fromItem || !toItem) return;

    setItemQuantities((prev) => {
      const movingQty = prev[fromKey] ?? 0;
      if (movingQty <= 0) return prev;

      const movingCoverage = itemCoverage(fromItem) * movingQty;
      const idealToQty = Math.ceil(movingCoverage / itemCoverage(toItem));
      const currentToQty = prev[toKey] ?? 0;
      const headroom =
        toItem.remaining_stock == null ? idealToQty : Math.max(toItem.remaining_stock - currentToQty, 0);
      const transferable = Math.min(idealToQty, headroom);

      if (transferable <= 0) {
        showToast(
          "warning",
          "Sem estoque para trocar",
          `${toItem.product_name} já está no limite de estoque disponível.`
        );
        return prev;
      }

      if (transferable < idealToQty) {
        showToast(
          "info",
          "Troca parcial",
          `Só havia estoque pra cobrir parte do volume com ${toItem.product_name}.`
        );
      }

      return {
        ...prev,
        [fromKey]: 0,
        [toKey]: currentToQty + transferable,
      };
    });
  }

  function handleDistribute() {
    const distribution = resetToRedistributed(result);
    setItemQuantities((prev) => {
      const next = { ...prev };
      products.forEach((item) => {
        next[keyFor(item.batch_id)] = distribution[item.batch_id] ?? 0;
      });
      return next;
    });
  }

  return (
    <Box
      ref={blockRef}
      bg="white"
      borderRadius="16px"
      border="1px solid rgba(0,74,51,0.08)"
      p={{ base: "14px", md: "18px" }}
      h="100%"
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb="10px" gap="8px">
        <Box>
          <Text fontWeight="900" fontSize="15px" color="#004a33">
            {result.label}
          </Text>
          <Text fontSize="12px" color="gray.500">
            Recomendado: {result.commercial_quantity} {result.commercial_unit_label}
            {isCase && !isCaseLiters && ` (${formatBaseAmount(result, result.needed_quantity)})`}
          </Text>
          {isBarrel && (
            <Text fontSize="11px" color="gray.400">
              Quantidade ajustada aos barris disponíveis no estoque
            </Text>
          )}
        </Box>
        {state === "ready" && isComplete && (
          <Text fontSize="11px" fontWeight="800" color="#2f9e44" whiteSpace="nowrap">
            ✓ Quantidade completa
          </Text>
        )}
      </Box>

      {state === "unavailable" && (
        <Text fontSize="13px" color="gray.500">
          Não conseguimos recomendar produtos para {result.label.toLowerCase()} no momento.
        </Text>
      )}

      {state === "pending" && (
        <Text fontSize="13px" color="gray.500">
          Ainda estamos preparando produtos compatíveis com essa categoria — você poderá
          adicioná-los em breve.
        </Text>
      )}

      {state === "ready" && products.length === 0 && (
        <Text fontSize="13px" color="gray.500">
          Sem estoque no momento para {result.label.toLowerCase()}.
        </Text>
      )}

      {state === "ready" && products.length > 0 && (
        <>
          <Progress
            value={Math.min((currentBase / result.needed_quantity) * 100, 100)}
            size="xs"
            borderRadius="99px"
            bg="rgba(0,74,51,0.1)"
            sx={{ "& > div": { background: isComplete ? "#2f9e44" : "#fc8f04" } }}
            mb="6px"
          />
          <Text fontSize="11px" color="gray.500" mb="10px">
            {isComplete
              ? isCase && !isCaseLiters
                ? `✓ Quantidade completa · ${currentCases} caixas (${formatBaseAmount(result, currentBase)})`
                : `✓ Quantidade completa · ${formatBaseAmount(result, currentBase)}`
              : `Faltam ${formatShortfall(result, currentBase)} para completar sua recomendação.`}
          </Text>

          {isCartBlocked && (
            <Text fontSize="11px" color="#b76800" bg="rgba(252,143,4,.08)" borderRadius="8px" px="8px" py="6px" mb="10px">
              O cadastro desse tipo de produto ainda está em ajuste — por enquanto, anote a quantidade e
              adicione esse item manualmente no carrinho.
            </Text>
          )}

          {expanded && isBarrel && barrelSizes.length > 1 && (
            <Wrap spacing="6px" mb="8px">
              <WrapItem>
                <Button
                  size="xs"
                  borderRadius="99px"
                  fontSize="11px"
                  fontWeight="700"
                  variant={sizeFilter == null ? "solid" : "outline"}
                  bg={sizeFilter == null ? "#004a33" : "transparent"}
                  color={sizeFilter == null ? "white" : "#004a33"}
                  borderColor="rgba(0,74,51,.25)"
                  onClick={() => setSizeFilter(null)}
                >
                  Todos
                </Button>
              </WrapItem>
              {barrelSizes.map((size) => (
                <WrapItem key={size}>
                  <Button
                    size="xs"
                    borderRadius="99px"
                    fontSize="11px"
                    fontWeight="700"
                    variant={sizeFilter === size ? "solid" : "outline"}
                    bg={sizeFilter === size ? "#004a33" : "transparent"}
                    color={sizeFilter === size ? "white" : "#004a33"}
                    borderColor="rgba(0,74,51,.25)"
                    onClick={() => setSizeFilter(size)}
                  >
                    {size / 1000} L
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
          )}

          {expanded && products.length > SEARCH_THRESHOLD && (
            <Box position="relative" mb="8px">
              <Box position="absolute" left="10px" top="50%" transform="translateY(-50%)" color="gray.400">
                <FiSearch size={14} />
              </Box>
              <Input
                size="sm"
                pl="30px"
                borderRadius="10px"
                placeholder="Buscar por nome ou marca"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
          )}

          <Box
            display="flex"
            flexDirection="column"
            mb="10px"
            maxH={expanded ? "420px" : "none"}
            overflowY={expanded ? "auto" : "visible"}
            pr={expanded ? "4px" : "0"}
            css={expanded ? {
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": { background: "rgba(0,74,51,.3)", borderRadius: "99px" },
            } : undefined}
          >
            {visibleList.map((item) => (
              <SuggestedItemRow
                key={item.batch_id}
                result={result}
                item={item}
                quantity={itemQuantities[keyFor(item.batch_id)] ?? 0}
                onChange={(value) => handleQtyChange(item.batch_id, value, item.remaining_stock)}
                alternatives={products
                  .filter((alt) => alt.batch_id !== item.batch_id && alt.sale_mode === item.sale_mode)
                  .map((alt) => {
                    const movingQty = itemQuantities[keyFor(item.batch_id)] ?? 0;
                    const movingCoverage = itemCoverage(item) * movingQty;
                    const idealQty = Math.ceil(movingCoverage / itemCoverage(alt));
                    const currentAltQty = itemQuantities[keyFor(alt.batch_id)] ?? 0;
                    const headroom =
                      alt.remaining_stock == null ? idealQty : Math.max(alt.remaining_stock - currentAltQty, 0);
                    const previewQty = Math.min(idealQty, headroom);
                    const previewPrice = alt.sale_mode === "case" ? alt.case_price : alt.unit_price;
                    return { ...alt, previewQty, previewSubtotal: previewQty * previewPrice };
                  })
                  .sort((a, b) => {
                    if (isBarrel && item.unit_volume_ml != null) {
                      const aMatch = a.unit_volume_ml === item.unit_volume_ml ? 0 : 1;
                      const bMatch = b.unit_volume_ml === item.unit_volume_ml ? 0 : 1;
                      if (aMatch !== bMatch) return aMatch - bMatch;
                    }
                    return (b.previewQty > 0) - (a.previewQty > 0);
                  })}
                onSwap={handleSwap}
              />
            ))}
            {expanded && visibleList.length === 0 && (
              <Text fontSize="12px" color="gray.500" py="10px">
                Nenhum produto encontrado.
              </Text>
            )}
          </Box>

          {(hasMore || expanded) && (
            <Button
              size="sm"
              variant="link"
              color="#004a33"
              fontWeight="700"
              fontSize="12px"
              mb="10px"
              onClick={() => {
                setExpanded((v) => !v);
                setSearch("");
                setSizeFilter(null);
              }}
            >
              {expanded ? "Mostrar menos" : `Ver todas as opções (${products.length})`}
            </Button>
          )}

          <Box>
            <Button
              size="sm"
              variant="outline"
              borderColor="rgba(0,74,51,.2)"
              color="#004a33"
              fontWeight="700"
              fontSize="12px"
              borderRadius="99px"
              onClick={handleDistribute}
            >
              Distribuir automaticamente
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
